import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import './Dashboard.css';
import './PerformanceDashboard.css';
import { utcDateKey, utcMonthKey, utcYearKey, utcWeekKey, weekKeyOf, localDateKey } from '../utils/dateWindow';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const periodOrder = ['daily', 'weekly', 'monthly', 'yearly'];
const periodLabels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' };

const initBlock = () => ({
  daily: { value: 0, target: 0, percent: 0, extra: null },
  weekly: { value: 0, target: 0, percent: 0, extra: null },
  monthly: { value: 0, target: 0, percent: 0, extra: null },
  yearly: { value: 0, target: 0, percent: 0, extra: null }
});

const pct = (a, t) => {
  if (!t || t === 0) return 0;
  return Math.round((a / t) * 100);
};

// Special percentage calculation for inventory turns
// where median (avg of min and max) is 100%, and deviation from median changes the percentage
const pctInventoryTurns = (actual, min, max) => {
  if (!min || !max || min === 0 || max === 0) return 0;

  const median = (min + max) / 2;

  // Calculate percentage based on actual relative to median target
  // This shows actual performance relative to the ideal median
  // Above median shows >100%, below shows <100%
  const percentage = Math.round((actual / median) * 100);

  return percentage;
};

// Abbreviate numbers: 91K, 1.2M, 3.4B, etc.
const abbreviateNumber = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
};

const formatCurrency = (v) => {
  if (v === undefined || v === null || Number.isNaN(v)) return '$0';
  return '$' + abbreviateNumber(Math.round(v));
};

const formatNumber = (v) => {
  if (v === undefined || v === null || Number.isNaN(v)) return '0';
  return abbreviateNumber(Number(v));
};

// Fiscal meta reused from sales dashboard
const getFiscalMeta = (dealership) => {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date();
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
};

// Get monthly target value from monthlyTargets array for current month
const getMonthlyTarget = (targets, field, currentMonth) => {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }
  // currentMonth is 0-based (0 = January), monthlyTargets uses 1-based (1 = January)
  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;
  return Number(monthData[field]) || 0;
};

// Sum monthly targets from fiscal year start to current month (YTD)
const getYTDTarget = (targets, field, dealership, referenceDate) => {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  const now = new Date(referenceDate);
  const currentMonth = now.getMonth(); // 0-based
  const fiscalStartMonth = dealership?.fiscalYearStartMonth || 1;
  const fiscalStartMonth0 = fiscalStartMonth - 1;

  // Determine which months to sum
  let monthsToSum = [];

  if (currentMonth >= fiscalStartMonth0) {
    // Same calendar year as fiscal start
    for (let m = fiscalStartMonth0; m <= currentMonth; m++) {
      monthsToSum.push(m + 1); // Convert back to 1-based for lookup
    }
  } else {
    // Fiscal year spans calendar years
    for (let m = fiscalStartMonth0; m < 12; m++) {
      monthsToSum.push(m + 1);
    }
    for (let m = 0; m <= currentMonth; m++) {
      monthsToSum.push(m + 1);
    }
  }

  // Sum the target values for all months
  let total = 0;
  for (const month of monthsToSum) {
    const monthData = targets.monthlyTargets.find(m => m.month === month);
    if (monthData) {
      total += Number(monthData[field]) || 0;
    }
  }
  return total;
};

// Convert target value based on frequency (fallback for old format)
const convertTargetValue = (targets, field, period, fiscalMeta) => {
  if (!targets) return 0;
  const base = Number(targets[field]) || 0;
  const storedFreq = (targets.fixedOpsFrequency || 'monthly').toLowerCase();

  let yearly;
  switch (storedFreq) {
    case 'daily':
      yearly = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearly = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearly = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearly = base;
  }

  switch (period) {
    case 'daily':
      return Math.round(yearly / fiscalMeta.daysInYear);
    case 'weekly':
      return Math.round(yearly / fiscalMeta.weeksInYear);
    case 'monthly':
      return Math.round(yearly / fiscalMeta.monthsInYear);
    case 'yearly':
    default:
      return Math.round(yearly);
  }
};

/** Technician capacity assumes a 5-day work week at 8 hours per technician per day. */
const HOURS_PER_TECH_DAY = 8;

/**
 * Share of a period's working days that must carry a Daily Technicians'
 * Performance entry before that reporting drives the Effective Labor Rate.
 * Mirrors MIN_TECH_REPORTING_COVERAGE in the Profit Summary controller so both
 * pages report the same rate.
 */
const MIN_TECH_REPORTING_COVERAGE = 0.5;

/** Count Mon–Fri days in [start, end] inclusive, ignoring the time component. */
const countWorkingDays = (start, end) => {
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  let count = 0;
  while (cursor <= last) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

// Get target for field based on period, preferring monthly targets
const getTargetForPeriod = (targets, field, period, dealership, referenceDate) => {
  const now = new Date(referenceDate);
  const currentMonth = now.getMonth(); // 0-based
  const currentYear = now.getFullYear();
  const fiscalMeta = getFiscalMeta(dealership);

  // If monthly targets exist, use them
  if (targets && targets.monthlyTargets && Array.isArray(targets.monthlyTargets)) {
    switch (period) {
      case 'daily': {
        const monthlyTarget = getMonthlyTarget(targets, field, currentMonth);
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        return Math.round(monthlyTarget / daysInMonth);
      }
      case 'weekly': {
        const monthlyTarget = getMonthlyTarget(targets, field, currentMonth);
        const weeksInMonth = 4.33; // average weeks per month
        return Math.round(monthlyTarget / weeksInMonth);
      }
      case 'monthly': {
        // MTD = just the current month's target
        return getMonthlyTarget(targets, field, currentMonth);
      }
      case 'yearly': {
        // YTD = sum from fiscal year start to current month
        return getYTDTarget(targets, field, dealership, referenceDate);
      }
      default:
        return 0;
    }
  }

  // Fallback to old frequency-based calculation
  return convertTargetValue(targets, field, period, fiscalMeta);
};

const percentClass = (p) => {
  if (p >= 80) return 'high';
  if (p >= 50) return 'mid';
  return 'low';
};

// Special color class for inventory turns where ideal is 100% (median)
// and deviation from 100% in either direction is bad
const percentClassInventoryTurns = (actual, min, max) => {
  if (!min || !max) return 'low';

  const median = (min + max) / 2;

  // If within the acceptable range (between min and max), it's at least yellow
  if (actual >= min && actual <= max) {
    // Calculate how close to median (closer to median = better)
    const distanceFromMedian = Math.abs(actual - median);
    const maxDistanceToEdge = (max - min) / 2;
    const deviationRatio = distanceFromMedian / maxDistanceToEdge;

    // If within 25% of median, it's green (high)
    // If between 25% and edge (100% deviation), it's yellow (mid)
    if (deviationRatio <= 0.25) return 'high';
    return 'mid';
  }

  // Outside the range is red (low)
  return 'low';
};

const calculateTrendIndicator = (actual, target, period, fiscalMeta, referenceDate = new Date()) => {
  if (!target || target === 0 || !fiscalMeta) return { status: 'neutral', color: 'rgba(255,255,255,0.5)' };
  // Pace the "expected progress" against the selected date so a past month compares
  // against that month's elapsed days, not today's.
  const today = new Date(referenceDate);
  let expectedProgressPercent = 0;
  const actualProgressPercent = (actual / target) * 100;
  switch (period) {
    case 'daily':
      expectedProgressPercent = 100;
      break;
    case 'weekly': {
      const dayOfWeek = today.getDay();
      const daysIntoWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      expectedProgressPercent = ((daysIntoWeek + 1) / 7) * 100;
      break;
    }
    case 'monthly': {
      const daysIntoMonth = today.getDate();
      const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      expectedProgressPercent = (daysIntoMonth / daysInCurrentMonth) * 100;
      break;
    }
    case 'yearly':
      expectedProgressPercent = (fiscalMeta.daysElapsed / fiscalMeta.daysInYear) * 100;
      break;
    default:
      expectedProgressPercent = 100;
  }
  const performanceRatio = actualProgressPercent / expectedProgressPercent;
  if (performanceRatio >= 0.9) return { status: 'onTrack', color: '#10b981' };
  if (performanceRatio >= 0.7) return { status: 'caution', color: '#f59e0b' };
  return { status: 'behind', color: '#ef4444' };
};

const TrendIndicator = ({ actual, target, period, fiscalMeta, referenceDate }) => {
  const trend = calculateTrendIndicator(actual, target, period, fiscalMeta, referenceDate);
  const isOnTrack = trend.status === 'onTrack';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginBottom: isOnTrack ? '2px' : '0px' }}>
        {isOnTrack ? (
          <polygon points="8,2 14,12 2,12" fill={trend.color} />
        ) : (
          <polygon points="2,4 14,4 8,14" fill={trend.color} />
        )}
      </svg>
      <span style={{ fontSize: '12px', color: trend.color, fontWeight: '600' }}>
        {trend.status === 'onTrack' ? 'On Track' : trend.status === 'caution' ? 'Caution' : 'Behind'}
      </span>
    </div>
  );
};

/* The status that colours a card. Where there is a target and a fiscal calendar
   this is the pace-adjusted reading the On Track / Caution / Behind indicator
   already shows, so the card and its indicator can never disagree — halfway
   through the month, half the target is on track, not "behind". Without a
   calendar to pace against it falls back to raw attainment. */
/* The meter and the percentage speak the same language as the card. Colouring
   them on raw attainment instead had 62% reading amber in the middle of a month
   the department was on track to finish — two scales contradicting each other on
   one card. The bar's WIDTH is still raw attainment; only its colour is paced. */
const statusToMeter = (status) =>
  status === 'ontrack' ? 'high' : status === 'caution' ? 'mid' : status === 'behind' ? 'low' : '';

const statusOf = (data, period, fiscalMeta, referenceDate) => {
  if (!data || !data.target) return 'neutral';
  if (fiscalMeta) {
    const { status } = calculateTrendIndicator(data.value, data.target, period, fiscalMeta, referenceDate);
    return status === 'onTrack' ? 'ontrack' : status;
  }
  const cls = percentClass(data.percent);
  return cls === 'high' ? 'ontrack' : cls === 'mid' ? 'caution' : 'behind';
};

export default function FixedOpsPerformanceDashboard({ user, managedOwner }) {
  const location = useLocation();
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [metricsByDealer, setMetricsByDealer] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Default to monthly alone: teams enter data at the end of the day and often
  // at the end of the week, so the shorter ranges sit blank or read as
  // under-performing for most of the period they cover. The other ranges stay
  // one click away in the View selector.
  const [selectedPeriods, setSelectedPeriods] = useState(['monthly']);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const isOwnerView = user?.role === 'OWNER' || managedOwner;

  const togglePeriod = (period) => {
    if (selectedPeriods.includes(period)) {
      // Only allow deselect if more than one is selected
      if (selectedPeriods.length > 1) {
        setSelectedPeriods(selectedPeriods.filter((p) => p !== period));
      }
    } else {
      // Add period (up to 4)
      if (selectedPeriods.length < 4) {
        setSelectedPeriods([...selectedPeriods, period].sort((a, b) => periodOrder.indexOf(a) - periodOrder.indexOf(b)));
      }
    }
  };

  useEffect(() => {
    // Check for dealershipId in location.state or query param
    let initialDealershipId = null;
    if (location && location.state && location.state.dealershipId) {
      initialDealershipId = location.state.dealershipId;
    } else if (location && location.search) {
      const params = new URLSearchParams(location.search);
      if (params.has('dealershipId')) {
        initialDealershipId = params.get('dealershipId');
      }
    }
    if (initialDealershipId) {
      setSelectedDealerships([initialDealershipId]);
    }
    load();
    // eslint-disable-next-line
  }, [managedOwner, selectedDate]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
      if (managedOwner && isOwnerView) {
        url += `?ownerId=${managedOwner.id}`;
      }

      const dsRes = await fetch(url, { headers });
      if (!dsRes.ok) throw new Error('Failed to load dealerships');
      const dsData = await dsRes.json();
      const dsList = dsData.dealerships || [];
      setDealerships(dsList);

      const entries = await Promise.all(
        dsList.map(async (d) => {
          const metrics = await fetchMetrics(d, headers, selectedDate);
          return [d.id, metrics];
        })
      );
      setMetricsByDealer(Object.fromEntries(entries));
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message || 'Failed to load fixed ops performance');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async (dealership, headers, referenceDate = new Date()) => {
    const dealershipId = dealership.id;
    const now = new Date(referenceDate);
    const todayIso = localDateKey(now);
    const month = now.getMonth();
    const year = now.getFullYear();
    const fiscalMeta = getFiscalMeta(dealership);

    // Period membership is decided from each row's UTC calendar date; the
    // reference period still comes from the date the user picked. Comparing
    // local components against UTC-stored dates dropped the 1st of the month
    // for any viewer west of UTC. Weeks here run Sunday through Saturday.
    const weekStart = weekKeyOf(localDateKey(now), 0);
    // Same Sunday as a local Date, for the working-day counts below, which read
    // local components like the month and year windows beside them do.
    const [wsY, wsM, wsD] = weekStart.split('-').map(Number);
    const weekStartDate = new Date(wsY, wsM - 1, wsD);
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    const yearKey = String(year);

    const isToday = (d) => utcDateKey(d) === todayIso;
    const isThisWeek = (d) => utcWeekKey(d, 0) === weekStart;
    const isThisMonth = (d) => utcMonthKey(d) === monthKey;
    const isThisYear = (d) => utcYearKey(d) === yearKey;

    const filterPeriod = (list) => ({
      daily: list.filter((e) => isToday(e.date)),
      weekly: list.filter((e) => isThisWeek(e.date)),
      monthly: list.filter((e) => isThisMonth(e.date)),
      yearly: list.filter((e) => isThisYear(e.date))
    });

    // fetch data
    const [apparelRes, partsRes, serviceRes, targetsRes, apparelInvRes, partsInvRes, salesRes] = await Promise.all([
      fetch(`${API_URL}/api/dealerships/${dealershipId}/apparel-licensing`, { headers }),
      fetch(`${API_URL}/api/dealerships/${dealershipId}/parts-sales`, { headers }),
      fetch(`${API_URL}/api/dealerships/${dealershipId}/service-jobs`, { headers }),
      fetch(`${API_URL}/api/targets/${dealershipId}`, { headers }),
      fetch(`${API_URL}/api/dealerships/${dealershipId}/apparel-inventory`, { headers }),
      fetch(`${API_URL}/api/dealerships/${dealershipId}/parts-inventory`, { headers }),
      fetch(`${API_URL}/api/dealerships/${dealershipId}/sales-activities`, { headers })
    ]);

    const apparelData = apparelRes.ok ? await apparelRes.json() : { entries: [] };
    const partsData = partsRes.ok ? await partsRes.json() : { entries: [] };
    const serviceData = serviceRes.ok ? await serviceRes.json() : { entries: [] };
    const targetsData = targetsRes.ok ? await targetsRes.json() : { fixedOps: {} };
    const apparelInvData = apparelInvRes.ok ? await apparelInvRes.json() : { entries: [] };
    const partsInvData = partsInvRes.ok ? await partsInvRes.json() : { entries: [] };
    const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };

    const targets = targetsData.fixedOps || {};

    const apparelEntries = (apparelData.entries || []).map((e) => ({
      date: e.date || e.saleDate,
      grossProfit: Number(e.grossProfit) || 0,
      netSales: Number(e.netSales) || 0,
      transactions: Number(e.transactions) || 0,
      items: Number(e.items || e.quantity || 0)
    }));

    const partsEntries = (partsData.entries || partsData.partsSales || []).map((p) => ({
      date: p.saleDate || p.date,
      grossProfit: Number(p.grossProfit) || 0,
      netSales: Number(p.totalRevenue || p.netSales || 0),
      transactions: Number(p.transactions) || 0,
      items: Number(p.items || p.quantity || 0)
    }));

    const serviceEntries = (serviceData.entries || serviceData.serviceJobs || []).map((s) => ({
      date: s.serviceDate || s.date,
      grossProfit: Number(s.grossProfit ?? 0),
      netSales: Number(s.totalRevenue || s.netSales || 0),
      // A Service RO is a repair order, not a row: a manual Service Input entry
      // summarises a whole day and carries that day's RO count. `entries` exposes
      // it as repairOrders; raw serviceJobs rows carry it as roNumber, the string
      // column it's stored in.
      transactions: Number(s.repairOrders ?? parseInt(s.roNumber, 10)) || 0,
      hours: Number(s.laborHours || s.hours || 0),
      actualHours: Number(s.actualHours || 0),
      billableHours: Number(s.billableHours || 0),
      availableHours: Number(s.availableHours || 0),
      numberOfTechnicians: Number(s.numberOfTechnicians || 0)
    }));

    const periods = ['daily', 'weekly', 'monthly', 'yearly'];

    const sumField = (list, field) => list.reduce((sum, i) => sum + (Number(i[field]) || 0), 0);

    const apparelBy = filterPeriod(apparelEntries);
    const partsBy = filterPeriod(partsEntries);
    const serviceBy = filterPeriod(serviceEntries);

    // Working days elapsed in each period window, matching the same ranges the
    // actuals above are filtered to. Feeds the Effective Labor Rate denominator.
    const workingDaysBy = {
      daily: countWorkingDays(now, now),
      weekly: countWorkingDays(weekStartDate, now),
      monthly: countWorkingDays(new Date(year, month, 1), now),
      yearly: countWorkingDays(new Date(year, 0, 1), now)
    };

    const buildBlock = (getter) => {
      const block = initBlock();
      periods.forEach((p) => {
        const val = getter(p);
        block[p].value = val.actual;
        block[p].target = val.target;
        block[p].percent = pct(val.actual, val.target);
        if (val.extra !== undefined) block[p].extra = val.extra;
      });
      return block;
    };

    // Net sales blocks
    const netSalesAll = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'netSales') + sumField(partsBy[p], 'netSales') + sumField(serviceBy[p], 'netSales');
      const apparelTarget = getTargetForPeriod(targets, 'apparelNetSales', p, dealership, referenceDate);
      const partsTarget = getTargetForPeriod(targets, 'partsNetSales', p, dealership, referenceDate);
      const serviceTarget = getTargetForPeriod(targets, 'serviceNetSales', p, dealership, referenceDate);
      const target = apparelTarget + partsTarget + serviceTarget;
      return { actual, target };
    });

    const netSalesMotor = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'netSales');
      const target = getTargetForPeriod(targets, 'apparelNetSales', p, dealership, referenceDate);
      return { actual, target };
    });

    const netSalesParts = buildBlock((p) => {
      const actual = sumField(partsBy[p], 'netSales');
      const target = getTargetForPeriod(targets, 'partsNetSales', p, dealership, referenceDate);
      return { actual, target };
    });

    const netSalesService = buildBlock((p) => {
      const actual = sumField(serviceBy[p], 'netSales');
      const target = getTargetForPeriod(targets, 'serviceNetSales', p, dealership, referenceDate);
      return { actual, target };
    });

    // Gross profit blocks
    const grossAll = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'grossProfit') + sumField(partsBy[p], 'grossProfit') + sumField(serviceBy[p], 'grossProfit');
      const apparelTarget = getTargetForPeriod(targets, 'apparelGrossProfit', p, dealership, referenceDate);
      const partsTarget = getTargetForPeriod(targets, 'partsGrossProfit', p, dealership, referenceDate);
      const serviceTarget = getTargetForPeriod(targets, 'serviceGrossProfit', p, dealership, referenceDate);
      const target = apparelTarget + partsTarget + serviceTarget;
      return { actual, target };
    });

    const grossMotor = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'grossProfit');
      const target = getTargetForPeriod(targets, 'apparelGrossProfit', p, dealership, referenceDate);
      return { actual, target };
    });

    const grossParts = buildBlock((p) => {
      const actual = sumField(partsBy[p], 'grossProfit');
      const target = getTargetForPeriod(targets, 'partsGrossProfit', p, dealership, referenceDate);
      return { actual, target };
    });

    const grossService = buildBlock((p) => {
      const actual = sumField(serviceBy[p], 'grossProfit');
      const target = getTargetForPeriod(targets, 'serviceGrossProfit', p, dealership, referenceDate);
      return { actual, target };
    });

    // Transactions
    const txAll = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'transactions') + sumField(partsBy[p], 'transactions') + sumField(serviceBy[p], 'transactions');
      const apparelTarget = getTargetForPeriod(targets, 'apparelTransactions', p, dealership, referenceDate);
      const partsTarget = getTargetForPeriod(targets, 'partsTransactions', p, dealership, referenceDate);
      const serviceTarget = getTargetForPeriod(targets, 'serviceTransactions', p, dealership, referenceDate);
      const target = apparelTarget + partsTarget + serviceTarget;
      return { actual, target };
    });

    const txMotor = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'transactions');
      const target = getTargetForPeriod(targets, 'apparelTransactions', p, dealership, referenceDate);
      return { actual, target };
    });

    const txParts = buildBlock((p) => {
      const actual = sumField(partsBy[p], 'transactions');
      const target = getTargetForPeriod(targets, 'partsTransactions', p, dealership, referenceDate);
      return { actual, target };
    });

    const txService = buildBlock((p) => {
      const actual = sumField(serviceBy[p], 'transactions');
      const target = getTargetForPeriod(targets, 'serviceTransactions', p, dealership, referenceDate);
      return { actual, target };
    });

    // Items
    const itemsAll = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'items') + sumField(partsBy[p], 'items') + sumField(serviceBy[p], 'hours');
      const apparelTarget = getTargetForPeriod(targets, 'apparelItems', p, dealership, referenceDate);
      const partsTarget = getTargetForPeriod(targets, 'partsItems', p, dealership, referenceDate);
      const serviceTarget = getTargetForPeriod(targets, 'serviceHours', p, dealership, referenceDate);
      const target = apparelTarget + partsTarget + serviceTarget;
      return { actual, target };
    });

    const itemsMotor = buildBlock((p) => {
      const actual = sumField(apparelBy[p], 'items');
      const target = getTargetForPeriod(targets, 'apparelItems', p, dealership, referenceDate);
      return { actual, target };
    });

    const itemsParts = buildBlock((p) => {
      const actual = sumField(partsBy[p], 'items');
      const target = getTargetForPeriod(targets, 'partsItems', p, dealership, referenceDate);
      return { actual, target };
    });

    const itemsService = buildBlock((p) => {
      const actual = sumField(serviceBy[p], 'hours');
      const target = getTargetForPeriod(targets, 'serviceHours', p, dealership, referenceDate);
      return { actual, target };
    });

    // Inventory: pick most recent entry in period per category
    const pickLatest = (list) => {
      if (!list || list.length === 0) return null;
      return [...list].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    };

    const buildInventoryBlock = (entries, targetField, minTarget, maxTarget) => {
      const block = initBlock();
      periods.forEach((p) => {
        // Inventory is entered weekly, so use weekly as minimum period
        // If daily is selected, show weekly data instead
        const effectivePeriod = p === 'daily' ? 'weekly' : p;

        const filtered = (entries || []).filter((e) => {
          const d = new Date(e.date);
          if (effectivePeriod === 'weekly') return isThisWeek(d);
          if (effectivePeriod === 'monthly') return isThisMonth(d);
          return isThisYear(d);
        });
        const latest = pickLatest(filtered);
        const turns = latest ? Number(latest.rollingAnnualTurns || latest.currentMonthlyTurns || 0) : 0;
        const total = latest ? Number(latest.inventoryTotal || 0) : 0;
        const grossMargin = latest && latest.netSales ? (latest.grossProfit || 0) / latest.netSales * 100 : null;

        // Use median of min and max as the target display value
        const target = (minTarget && maxTarget) ? (minTarget + maxTarget) / 2 : 0;

        // Calculate special percentage for inventory turns
        const percent = (minTarget && maxTarget) ? pctInventoryTurns(turns, minTarget, maxTarget) : 0;

        block[p].value = turns;
        block[p].target = target;
        block[p].percent = percent;
        block[p].extra = { total, grossMargin, min: minTarget, max: maxTarget };
      });
      return block;
    };

    // Get inventory turns targets from fixedOps targets
    const inventoryTurns = targets.inventoryTurns || {};
    const apparelMin = Number(inventoryTurns.apparelMin) || 2;
    const apparelMax = Number(inventoryTurns.apparelMax) || 4;
    const partsMin = Number(inventoryTurns.partsMin) || 3;
    const partsMax = Number(inventoryTurns.partsMax) || 5;

    const invApparel = buildInventoryBlock(apparelInvData.entries || [], 'apparelInventoryValue', apparelMin, apparelMax);
    const invParts = buildInventoryBlock(partsInvData.entries || [], 'partsInventoryValue', partsMin, partsMax);

    const inventoryAll = initBlock();
    // For total inventory, use combined min/max from both parts and apparel
    const totalMin = Math.min(partsMin, apparelMin);
    const totalMax = Math.max(partsMax, apparelMax);
    periods.forEach((p) => {
      const value = invApparel[p].value + invParts[p].value;
      const target = (totalMin + totalMax) / 2; // Use median of combined range
      const total = (invApparel[p].extra?.total || 0) + (invParts[p].extra?.total || 0);
      const grossMargin = null; // not available on inventory rows; show N/A
      const percent = pctInventoryTurns(value, totalMin, totalMax);
      inventoryAll[p] = { value, target, percent, extra: { total, grossMargin, min: totalMin, max: totalMax } };
    });

    // Calculate Fixed Operations sold with bikes metrics. When the dealership
    // disables "always show F&I", unclosed deals don't count toward report
    // figures until they're marked closed/committed.
    const salesActivities = (dealership.showUnclosedFI !== false)
      ? (salesData.activities || [])
      : (salesData.activities || []).filter((a) => a.closed);
    const salesBy = filterPeriod(salesActivities.map(a => ({
      date: a.saleDate,
      fiPartsAmount: Number(a.fiPartsAmount) || 0,
      fiServiceAmount: Number(a.fiServiceAmount) || 0,
      fiApparelAmount: Number(a.fiApparelAmount) || 0
    })));

    const calculateFixedOpsMetric = (fiField, standaloneBy, revenueField) => {
      const metric = initBlock();

      periods.forEach(period => {
        // Sum F&I amounts from sales activities
        const fiTotal = sumField(salesBy[period], fiField);

        // Sum standalone revenue/net sales
        const standaloneTotal = sumField(standaloneBy[period], revenueField);

        const totalValue = fiTotal + standaloneTotal;

        metric[period].value = fiTotal; // Achieved = F&I only
        metric[period].target = totalValue; // "Total" = F&I + standalone
        metric[period].percent = totalValue > 0 ? Math.round((fiTotal / totalValue) * 100) : 0;
      });

      return metric;
    };

    const fiParts = calculateFixedOpsMetric('fiPartsAmount', partsBy, 'netSales');
    const fiService = calculateFixedOpsMetric('fiServiceAmount', serviceBy, 'netSales');
    const fiApparel = calculateFixedOpsMetric('fiApparelAmount', apparelBy, 'netSales');

    // Effective Labor Rate: Service Net Sales ÷ staffed technician capacity, where
    // capacity is working days × 8 hours × technician count. Matches the Profit Summary
    // definition so both pages report the same rate. The target is the configured
    // Labor Rate.
    //
    // Headcount prefers what was reported under Daily Technicians' Performance in
    // Service Input, but only where that reporting is consistent enough to describe
    // the period: the count is taken per day and used only when at least half the
    // working days carry one. Sparse periods fall back to the Targets headcount.
    const targetTechnicians = Number(targets.serviceTechnicians) || 0;
    const techniciansForPeriod = (p) => {
      const byDay = new Map();
      serviceBy[p].forEach((e) => {
        if (!(e.numberOfTechnicians > 0)) return;
        const day = new Date(e.date).toISOString().slice(0, 10);
        byDay.set(day, Math.max(byDay.get(day) ?? 0, e.numberOfTechnicians));
      });
      const workingDays = workingDaysBy[p];
      const coverage = workingDays > 0 ? byDay.size / workingDays : 0;
      if (coverage < MIN_TECH_REPORTING_COVERAGE) return targetTechnicians;
      return [...byDay.values()].reduce((sum, techs) => sum + techs, 0) / byDay.size;
    };

    const effectiveLaborRate = buildBlock((p) => {
      const actualNetSales = sumField(serviceBy[p], 'netSales');
      const technicians = techniciansForPeriod(p);
      const capacityHours = workingDaysBy[p] * HOURS_PER_TECH_DAY * technicians;
      const actual = capacityHours > 0 ? actualNetSales / capacityHours : 0;
      const target = Number(targets.laborRate) || 0;

      return {
        actual,
        target,
        extra: { workingDays: workingDaysBy[p], technicians, capacityHours }
      };
    });

    // Labor Analysis metrics from Daily Technicians' Performance
    // Productivity = Total Actual Hours / Total Available Hours
    const laborProductivity = buildBlock((p) => {
      const totalActual = sumField(serviceBy[p], 'actualHours');
      const totalAvailable = sumField(serviceBy[p], 'availableHours');
      const actual = totalAvailable > 0 ? (totalActual / totalAvailable) * 100 : 0;
      return { actual, target: 100, extra: { actualHours: totalActual, availableHours: totalAvailable } };
    });

    // Efficiency = Total Actual Hours / Total Billable Hours
    const laborEfficiency = buildBlock((p) => {
      const totalActual = sumField(serviceBy[p], 'actualHours');
      const totalBillable = sumField(serviceBy[p], 'billableHours');
      const actual = totalBillable > 0 ? (totalActual / totalBillable) * 100 : 0;
      return { actual, target: 100, extra: { actualHours: totalActual, billableHours: totalBillable } };
    });

    // Proficiency = Total Billable Hours / Total Available Hours
    const laborProficiency = buildBlock((p) => {
      const totalBillable = sumField(serviceBy[p], 'billableHours');
      const totalAvailable = sumField(serviceBy[p], 'availableHours');
      const actual = totalAvailable > 0 ? (totalBillable / totalAvailable) * 100 : 0;
      return { actual, target: 100, extra: { billableHours: totalBillable, availableHours: totalAvailable } };
    });

    // Lost Time = Total Available Hours - Total Actual Hours
    const lostTime = buildBlock((p) => {
      const totalActual = sumField(serviceBy[p], 'actualHours');
      const totalAvailable = sumField(serviceBy[p], 'availableHours');
      const actual = totalAvailable > 0 ? totalAvailable - totalActual : 0;
      return { actual, target: 0 };
    });

    // Lost Opportunity = (Total Available Hours - Total Actual Hours) * Labor Rate
    const laborRate = targets.laborRate || 0;
    const lostOpportunity = buildBlock((p) => {
      const totalActual = sumField(serviceBy[p], 'actualHours');
      const totalAvailable = sumField(serviceBy[p], 'availableHours');
      const actual = totalAvailable > 0 ? (totalAvailable - totalActual) * laborRate : 0;
      return { actual, target: 0 };
    });

    return {
      netSalesAll,
      netSalesMotor,
      netSalesParts,
      netSalesService,
      grossAll,
      grossMotor,
      grossParts,
      grossService,
      txAll,
      txMotor,
      txParts,
      txService,
      itemsAll,
      itemsMotor,
      itemsParts,
      itemsService,
      inventoryAll,
      invApparel,
      invParts,
      fiParts,
      fiService,
      fiApparel,
      effectiveLaborRate,
      laborProductivity,
      laborEfficiency,
      laborProficiency,
      lostTime,
      lostOpportunity,
      fiscalMeta,
      hasTargets: Array.isArray(targets.monthlyTargets) && targets.monthlyTargets.length > 0,
    };
  };

  const filteredDealerships = useMemo(() => {
    if (!selectedDealerships || selectedDealerships.length === 0) return dealerships;
    return dealerships.filter((d) => selectedDealerships.includes(d.id));
  }, [dealerships, selectedDealerships]);

  const toggleDealership = (id) => {
    setSelectedDealerships((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));
  };

  const selectAll = () => setSelectedDealerships(dealerships.map((d) => d.id));
  const clearAll = () => setSelectedDealerships([]);

  // One range selected is the normal case, and then the range is already named
  // once in the View selector — repeating "Monthly" on all twenty-six cards was
  // noise. With two or more selected each block has to name itself again.
  const multiPeriod = selectedPeriods.length > 1;

  // With a single range the whole card takes that range's status. With several
  // there is no one status for the card, so each block carries its own.
  const cardStatus = (block, fiscalMeta) =>
    multiPeriod ? 'neutral' : statusOf(block[selectedPeriods[0]], selectedPeriods[0], fiscalMeta, selectedDate);

  const renderKpi = (title, block, formatter = formatNumber, extraRenderer, fiscalMeta, variant) => {
    const isHero = variant === 'hero';
    return (
      <div className={`kpi-card${isHero ? ' perf-hero' : ''}`} data-status={cardStatus(block, fiscalMeta)}>
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`fixedops-periods${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map((period) => {
            const data = block[period];
            const status = statusOf(data, period, fiscalMeta, selectedDate);
            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? status : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value">{formatter(data.value)}</div>
                <div className="kpi-target">Target: {formatter(data.target)}</div>
                <div className={`kpi-percent ${statusToMeter(status)}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${statusToMeter(status)}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                {/* Only the total spells the status out. On every card the words
                    repeated what the colour already says twenty-six times. */}
                {isHero && fiscalMeta && (
                  <TrendIndicator actual={data.value} target={data.target} period={period} fiscalMeta={fiscalMeta} referenceDate={selectedDate} />
                )}
                {extraRenderer && extraRenderer(data)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderInventory = (title, block, fiscalMeta, variant) => {
    const renderExtra = (data) => (
      <div className="kpi-extra" style={{ marginTop: '8px' }}>
        <div>Total: {formatCurrency(data.extra?.total || 0)}</div>
        <div>Gross Margin: {data.extra?.grossMargin !== null && data.extra?.grossMargin !== undefined ? `${Math.round(data.extra.grossMargin)}%` : 'N/A'}</div>
      </div>
    );
    // Turns are good near the middle of the band, not simply high, so the card
    // takes its status from that same min/max reading rather than from pace.
    const turnsStatus = (period) => {
      const data = block[period];
      const { min, max } = data.extra || {};
      if (!min || !max) return statusOf(data, period, fiscalMeta, selectedDate);
      const cls = percentClassInventoryTurns(data.value, min, max);
      return cls === 'high' ? 'ontrack' : cls === 'mid' ? 'caution' : 'behind';
    };
    return (
      <div className={`kpi-card${variant === 'hero' ? ' perf-hero' : ''}`} data-status={multiPeriod ? 'neutral' : turnsStatus(selectedPeriods[0])}>
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`fixedops-periods${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map((period) => {
            const data = block[period];
            // Same reading the card takes, so the figure and the bar can never
            // disagree with the colour of the card they sit on.
            const colorClass = statusToMeter(turnsStatus(period));

            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? turnsStatus(period) : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value">{formatNumber(data.value)}</div>
                <div className="kpi-target">Target: {formatNumber(data.target)}</div>
                <div className={`kpi-percent ${colorClass}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${colorClass}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                {variant === 'hero' && fiscalMeta && (
                  <TrendIndicator actual={data.value} target={data.target} period={period} fiscalMeta={fiscalMeta} referenceDate={selectedDate} />
                )}
                {renderExtra(data)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Custom render for Fixed Operations - shows "Total" instead of "Target" and percentage is F&I/Total
  // A share of a total, not progress against a goal, so there is no on/off-track
  // status to colour it with. It stays neutral on purpose.
  const renderFixedOpsKpi = (title, block, formatter = formatCurrency) => {
    return (
      <div className="kpi-card" data-status="neutral">
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`fixedops-periods${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map((period) => {
            const data = block[period];
            return (
              <div key={period} className="kpi-period-block">
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value">{formatter(data.value)}</div>
                <div className="kpi-target">Total: {formatter(data.target)}</div>
                <div className="kpi-percent">{data.percent}% with bikes</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* A section is one total and its parts. The total gets its own column so the
     relationship is legible; where a section has no total the parts fill the
     row instead. */
  const renderSection = (title, subtitle, hero, breakouts) => (
    <section className="perf-section" key={title}>
      <div className="perf-section-head">
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>
      <div className={`perf-section-body kpi-compact${hero ? '' : ' no-hero'}`}>
        {hero}
        <div className="perf-breakouts">{breakouts}</div>
      </div>
    </section>
  );

  /* The section totals, held at the top of the page. Reading down the strip
     answers "how is fixed ops doing" without scrolling through the detail. */
  const renderSummary = (m) => {
    const fm = m.fiscalMeta;
    const p = selectedPeriods[0];
    const items = [
      ['Net Sales', m.netSalesAll, formatCurrency, fm],
      ['Gross Profit', m.grossAll, formatCurrency, fm],
      ['Transactions', m.txAll, formatNumber, fm],
      ['Items / Hours', m.itemsAll, formatNumber, fm],
      ['Inventory Turns', m.inventoryAll, formatNumber, fm],
      ['Eff. Labor Rate', m.effectiveLaborRate, formatCurrency, fm],
    ];
    return (
      <div className="perf-summary">
        {items.map(([label, block, fmt, meta]) => {
          const data = block[p];
          if (!data) return null;
          return (
            <div className="perf-summary-item" key={label} data-status={multiPeriod ? 'neutral' : statusOf(data, p, meta, selectedDate)}>
              <div className="perf-summary-label">{label}</div>
              <div className="perf-summary-value">{fmt(data.value)}</div>
              <div className="perf-summary-meta">
                <span className="perf-pct">{data.percent}%</span>
                of {fmt(data.target)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="placeholder-view"><p>Loading fixed ops performance...</p></div>;
  if (error) return <div className="placeholder-view error"><p>{error}</p></div>;

  if (filteredDealerships.length > 0 && filteredDealerships.every(d => !metricsByDealer[d.id]?.hasTargets)) {
    return <NoTargetsPlaceholder />;
  }

  return (
    <div className="dashboard-view compact-dash perf-dash">
      <div className="view-header">
        <div>
          <h1 className="view-title">Fixed Operations Performance</h1>
          <p className="view-subtitle">{selectedPeriods.map((p) => periodLabels[p]).join(' & ')} performance across dealerships</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar sales-performance">
        <div className="filter-bar-main">
          <button 
            className={`filter-button ${selectedDealerships.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {selectedDealerships.length > 0 && ` (${selectedDealerships.length})`}
          </button>

          <div className="period-selector">
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginRight: '8px' }}>View:</span>
            {periodOrder.map((p) => (
              <button key={p} className={`period-btn ${selectedPeriods.includes(p) ? 'active' : ''}`} onClick={() => togglePeriod(p)}>
                {periodLabels[p]}
              </button>
            ))}
          </div>

          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
        </div>

        {selectedDealerships.length > 0 && (
          <div className="active-filters">
            {dealerships.filter((d) => selectedDealerships.includes(d.id)).map((d) => (
              <div key={d.id} className="active-filter-chip">
                {d.name}
                <button onClick={() => toggleDealership(d.id)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>
            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={selectedDealerships.length === 0 ? selectAll : clearAll} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map((dealership) => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter((d) => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* KPIs */}
      {filteredDealerships.length <= 1 ? (
        /* Single dealership: one card, dealer name once, compact sub-headers per
           section (mirrors the single-dealer Sales view). */
        filteredDealerships.map((d) => {
          const m = metricsByDealer[d.id];
          if (!m) return null;
          const fm = m.fiscalMeta;
          return (
            <div key={`single-${d.id}`}>
              {renderSummary(m)}
              <h2 className="perf-dealer-name">{d.name}</h2>

              {renderSection(
                'Net Sales',
                'Total and category breakouts',
                renderKpi('Total Fixed Ops Net Sales', m.netSalesAll, formatCurrency, undefined, fm, 'hero'),
                [
                  renderKpi('Apparel', m.netSalesMotor, formatCurrency, undefined, fm),
                  renderKpi('Parts', m.netSalesParts, formatCurrency, undefined, fm),
                  renderKpi('Service', m.netSalesService, formatCurrency, undefined, fm),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Gross Profit',
                'Total and category breakouts',
                renderKpi('Total Fixed Ops GP', m.grossAll, formatCurrency, undefined, fm, 'hero'),
                [
                  renderKpi('Apparel', m.grossMotor, formatCurrency, undefined, fm),
                  renderKpi('Parts', m.grossParts, formatCurrency, undefined, fm),
                  renderKpi('Service', m.grossService, formatCurrency, undefined, fm),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Transactions',
                'Transactions to goal by category',
                renderKpi('Total Transactions', m.txAll, formatNumber, undefined, fm, 'hero'),
                [
                  renderKpi('Apparel Tx', m.txMotor, formatNumber, undefined, fm),
                  renderKpi('Parts Tx', m.txParts, formatNumber, undefined, fm),
                  renderKpi('Service RO', m.txService, formatNumber, undefined, fm),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Items / Hours',
                'Units and hours versus goal',
                renderKpi('Total Items/Hours', m.itemsAll, formatNumber, undefined, fm, 'hero'),
                [
                  renderKpi('Apparel Items', m.itemsMotor, formatNumber, undefined, fm),
                  renderKpi('Parts Items', m.itemsParts, formatNumber, undefined, fm),
                  renderKpi('Service Hours', m.itemsService, formatNumber, undefined, fm),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Inventory Turns',
                'Turns to goal with inventory value and gross margin',
                renderInventory('Total Inventory Turns', m.inventoryAll, fm, 'hero'),
                [
                  renderInventory('Apparel Turns', m.invApparel, fm),
                  renderInventory('Parts Turns', m.invParts, fm),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Fixed operations sold with new/used unit',
                'Parts, service, and apparel sold with bikes',
                null,
                [
                  renderFixedOpsKpi('Parts & Accessories', m.fiParts, formatCurrency),
                  renderFixedOpsKpi('Service', m.fiService, formatCurrency),
                  renderFixedOpsKpi('Apparel & Licensing', m.fiApparel, formatCurrency),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}

              {renderSection(
                'Labor Analysis',
                'Effective labor rate, productivity, efficiency, and proficiency',
                renderKpi('Effective Labor Rate', m.effectiveLaborRate, formatCurrency, undefined, fm, 'hero'),
                [
                  renderKpi('Productivity', m.laborProductivity, (v) => v.toFixed(1) + '%'),
                  renderKpi('Efficiency', m.laborEfficiency, (v) => v.toFixed(1) + '%'),
                  renderKpi('Proficiency', m.laborProficiency, (v) => v.toFixed(1) + '%'),
                  renderKpi('Lost Time (hrs)', m.lostTime, (v) => v.toFixed(1)),
                  renderKpi('Lost Opportunity', m.lostOpportunity, formatCurrency),
                ].map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)
              )}
            </div>
          );
        })
      ) : (
        <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Net Sales</h2>
          <p className="card-subtitle">Overall fixed ops and category breakouts</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`ns-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderKpi('Total Fixed Ops Net Sales', m.netSalesAll, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Apparel Net Sales', m.netSalesMotor, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Parts Net Sales', m.netSalesParts, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Service Net Sales', m.netSalesService, formatCurrency, undefined, m.fiscalMeta)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Gross Profit</h2>
          <p className="card-subtitle">Overall fixed ops and category breakouts</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`gp-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderKpi('Total Fixed Ops GP', m.grossAll, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Apparel GP', m.grossMotor, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Parts GP', m.grossParts, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Service GP', m.grossService, formatCurrency, undefined, m.fiscalMeta)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Transactions</h2>
          <p className="card-subtitle">Transactions to goal by category</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`tx-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderKpi('Total Transactions', m.txAll, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Apparel Tx', m.txMotor, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Parts Tx', m.txParts, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Service RO', m.txService, formatNumber, undefined, m.fiscalMeta)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Items / Hours</h2>
          <p className="card-subtitle">Units and hours versus goal</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`items-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderKpi('Total Items/Hours', m.itemsAll, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Apparel Items', m.itemsMotor, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Parts Items', m.itemsParts, formatNumber, undefined, m.fiscalMeta)}
                {renderKpi('Service Hours', m.itemsService, formatNumber, undefined, m.fiscalMeta)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Inventory Turns</h2>
          <p className="card-subtitle">Turns to goal with inventory value and gross margin</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`inv-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderInventory('Total Inventory Turns', m.inventoryAll, m.fiscalMeta)}
                {renderInventory('Apparel Turns', m.invApparel, m.fiscalMeta)}
                {renderInventory('Parts Turns', m.invParts, m.fiscalMeta)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Fixed Operations sold with new/used unit</h2>
          <p className="card-subtitle">Parts, Service, and Apparel sold with bikes</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`fixedops-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderFixedOpsKpi('Parts & Accessories', m.fiParts, formatCurrency)}
                {renderFixedOpsKpi('Service', m.fiService, formatCurrency)}
                {renderFixedOpsKpi('Apparel & Licensing', m.fiApparel, formatCurrency)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Labor Analysis</h2>
          <p className="card-subtitle">Effective labor rate, productivity, efficiency, and proficiency</p>
        </div>
        <div className="kpi-compare-row">
          {filteredDealerships.map((d) => {
            const m = metricsByDealer[d.id];
            if (!m) return null;
            return (
              <div key={`labor-rate-${d.id}`} className="kpi-compact kpi-sales-group">
                <div className="kpi-dealer-label">{d.name}</div>
                {renderKpi('Effective Labor Rate', m.effectiveLaborRate, formatCurrency, undefined, m.fiscalMeta)}
                {renderKpi('Productivity', m.laborProductivity, (v) => v.toFixed(1) + '%')}
                {renderKpi('Efficiency', m.laborEfficiency, (v) => v.toFixed(1) + '%')}
                {renderKpi('Proficiency', m.laborProficiency, (v) => v.toFixed(1) + '%')}
                {renderKpi('Lost Time (hrs)', m.lostTime, (v) => v.toFixed(1))}
                {renderKpi('Lost Opportunity', m.lostOpportunity, formatCurrency)}
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
